import { Component, computed, inject, input, signal } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import { TransitionRequestDto, UserDetailedDto, UsersClient, UserUpdateDto } from '@unity/clients';
import { TextboxReadonlyControlComponent } from '@unity/components/forms/textbox-readonly-control/textbox-readonly-control.component';
import { FormLayoutComponent } from '@unity/components/layouts/form-layout/form-layout.component';
import { AuthorizeForInternalDirective } from '@unity/directives/authorize-for-internal.directive';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ImpersonationService } from '@unity/services/impersonation.service';
import { SessionService } from '@unity/services/session.service';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { DialogConfirmSharedComponent } from '@unity/shared/composed/dialog-confirm-shared/dialog-confirm-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { GridAgreementSharedComponent } from '@unity/shared/grid-agreement-shared/grid-agreement-shared.component';
import { GridUserRoleSharedComponent } from '@unity/shared/grid-user-role-shared/grid-user-role-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY, filter } from 'rxjs';

class UserUpdateSchema implements FormGroupControlSchema<UserUpdateDto> {
  public full_name: FormControl<string>;

  public constructor(user?: UserDetailedDto) {
    this.full_name = stringFormControl({ disabled: true, value: user?.full_name });
  }
}

@Component({
  selector: 'unity-manage-user-page',
  standalone: true,
  imports: [
    KENDO_STACKLAYOUT,
    ReactiveFormsModule,
    AuthorizeForInternalDirective,
    PanelManageSharedComponent,
    GridUserRoleSharedComponent,
    PageContainerComponent,
    TextboxReadonlyControlComponent,
    TextboxControlComponent,
    AuthorizeForInternalDirective,
    GridAgreementSharedComponent,
    FormLayoutComponent,
    DialogConfirmSharedComponent,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './manage-user-page.component.html',
  styleUrl: './manage-user-page.component.scss',
})
export class ManageUserPageComponent extends AbstractManageComponent<UserDetailedDto, UserUpdateDto, UserUpdateSchema> {
  private readonly sessionService = inject(SessionService);
  private readonly impersonationService = inject(ImpersonationService);
  private readonly usersClient = inject(UsersClient);

  public readonly $userId = input.required({ alias: 'userId', transform: (value: string) => parseInt(value) });

  protected readonly $canEditStatus = computed(() => this.$isMe() === false);

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      return resourceId ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.usersClient.getOneAsDetailed(resourceId),
  });

  protected override updateResourceFn$ = (resourceId: number, form: FormGroup<UserUpdateSchema>) =>
    this.usersClient.updateOne(resourceId, form.getRawValue());

  protected override deleteResourceFn$ = (_: number) => EMPTY;

  protected override availableTransitionsFn$ = (resourceId: number) => this.availableTransitionsClient.getAllForUserAsMini(resourceId);

  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) =>
    this.transitionsClient.setForUser(resourceId, transition);

  protected override newControlsFn(dto?: UserDetailedDto) {
    return new UserUpdateSchema(dto);
  }

  protected override editEntitlement: UnityEntitlementPath = 'User:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'User:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'User:Delete';
  protected override resourceName = 'User';
  protected override $resourceId = this.$userId;
  protected override readonly $contentHeader = computed(() => `User: ${this.$resource()?.full_name}`);

  protected user$ = toObservable(this.$resource).pipe(filter(user => !!user));

  protected readonly $impersonationError = signal<boolean>(false);

  protected readonly $myUserFullname = computed(() => this.sessionService.$user().fullname);

  protected readonly $assignUserToBPAction = computed(() => {
    const dto = this.$resource();
    if (!dto) {
      return;
    }
    return QuickActions.AssignUserToBusinessPartner(dto.id);
  });

  protected readonly $endImpersonationAction = computed(() => {
    const isImpersonating = this.sessionService.$isImpersonating();
    const dto = this.$resource();
    const isMe = this.$isMe();
    if (!dto || !isImpersonating || !isMe) {
      return;
    }
    return QuickActions.EndImpersonation(() => this.impersonationService.stopImpersonating());
  });

  protected readonly $impersonateUserAction = computed(() => {
    const dto = this.$resource();
    const isMe = this.$isMe();

    if (!dto || isMe) {
      return;
    }
    return QuickActions.ImpersonateUser(() => this.onImpersonate());
  });

  protected readonly $isMe = computed(() => {
    const dto = this.$resource();
    const me = this.sessionService.$user();
    if (!dto) {
      return;
    }
    return me.user_id === dto.id;
  });

  protected impersonate = () => {
    const user = this.$resource();
    if (!user) {
      return;
    }
    this.impersonationService.startImpersonating(user.user_name);
  };

  protected closeErrorDialog() {
    this.$impersonationError.set(false);
  }

  protected switchImpersonation() {
    const user = this.$resource();
    if (!user) {
      return;
    }
    this.impersonationService.switchImpersonationTo(user.user_name);
  }

  private onImpersonate() {
    if (this.sessionService.$isImpersonating() == true) {
      this.$impersonationError.set(true);
    } else {
      this.impersonate();
    }
  }

  protected returnToDashboardAction = QuickActions.ReturnToDashboard;
}
